import {Box3, BoxGeometry, Mesh, Quaternion, Vector3} from "three";
import * as Globals from "../../globals";
import {buildingGroup, wallMeshHBE_L} from "../../main";
import {setObjectLayer} from "../../utils/layerUtils";
import {clearGroup, raycaster, render} from "../../sceneManager";
import {addEdgeGeometry, createGroup, findChildByName, getLengthByAxis} from "../../utils/threeUtils";
import {getWallGroupParent, isRoofOrFloor} from "../../utils/configuratorUtils";
import {toFixedFloat} from "../../utils/utils";
import {createWallSegmentWithCutOuts, cutWallWithMesh, restoreWallLayers} from "../walls/wall";
import {controlObjectMaterial, lineMaterial} from "../../constants/materialConstants";
import {recalculateBuildingParameters} from "../../calculators/buildingParameters/parameterCalculator";
import {controlObjectName, cutOutsGroupName} from "../../constants/nameConstants";
import {structuralWallHeight} from "./structuralWallHeight";

export class CutOut {
    constructor(cutOutPreview) {
        this.width = cutOutPreview.width;
        this.height = cutOutPreview.height;
        this.bottomOffset = cutOutPreview.bottomOffset;
        this.position = cutOutPreview.object.position;
        this.controlObject = cutOutPreview.object; //Objekt zum Kontrollieren des erstellten Ausschnitts
        this.placeable = cutOutPreview.placeable; //Ist platzierbar?: Verhindert setzen an unerlaubten Positionen
        this.cutMesh = null; //Mesh to cut
        this.cutOutGroup = null;
        this.recreated = cutOutPreview.recreated;
        this.marginLeftRight = cutOutPreview.marginLeftRight;
    }

    static cutWallWithCutMesh(cutWall, cutMesh, elements = []) {
        let wallElements = [];
        if (elements.length === 0) {
            for (let i = 0; i < cutWall.children.length; i++) {
                let child = cutWall.children[i];
                if (child.name === cutOutsGroupName || child.userData.type === "wallCollisionLine") {
                    continue;
                }
                wallElements.push(cutWall.children[i]);
            }
        } else {
            wallElements = elements;
        }

        cutWallWithMesh(wallElements, cutMesh, true, false, true, elements.length !== 0);
        restoreWallLayers(wallElements);
    }

    static clearOldCutOut(control) {
        //Original Parent in aktueller Szene suchen
        let originalParent = buildingGroup.getObjectByProperty("uuid", control.userData.originalParent);
        if (originalParent === undefined) {
            return null;
        }

        let wallGroup = originalParent.parent.parent; //Original wall parent save

        //Entfernen der Gruppe aus dem original Parent
        clearGroup(originalParent);
        originalParent.removeFromParent();
        return wallGroup;
    }

    static restoreCutOut(wallGroup, cutOut, elements = []) {
        let cutMesh = null;

        for (let j = 0; j < cutOut.children.length; j++) {
            let child = cutOut.children[j];
            if (child.name.includes("CutMesh")) {
                cutMesh = child;
                break;
            }
        }

        if (cutMesh === null) {
            console.error("No cut mesh for cutout found.");
            return;
        }

        CutOut.cutWallWithCutMesh(wallGroup, cutMesh, elements);
    }

    static adjustWall(wallGroup) {
        let data = wallGroup.userData;

        //Neue Wand erstellen, positionieren und in die Szene einfügen
        let newWall = createWallSegmentWithCutOuts(wallGroup, data.length, data.height, wallMeshHBE_L, Globals.layers.wall, true, true, data.direction, data.isOuterWall);

        //Alte Wand entfernen
        clearGroup(wallGroup);
        wallGroup.removeFromParent();
        return newWall;
    }

    create(wallGroup = null, wallMeshName = null) {
        if (!this.placeable) {
            return false;
        }

        let wallMesh = wallGroup === null ? this.getMeshToCut() : findChildByName(wallGroup, wallMeshName);
        if (wallMesh === null) {
            return false;
        }

        wallGroup = getWallGroupParent(wallMesh); //Get the wallGroup the element is currently hovering on

        let adjustOriginalWall = false;
        let originalWallGroup = null;
        //Wenn der CutOut recreated ist muss geprüft werden ob die Wand die gleiche wie vorher ist sonst muss die alte Wand auch nachher angepasst werden
        if (this.recreated) {
            originalWallGroup = CutOut.clearOldCutOut(this.controlObject);
            adjustOriginalWall = wallGroup !== originalWallGroup;
        }

        if (wallGroup === null) {
            return false;
        }


        let bounds = new Box3().setFromObject(wallMesh);
        //TODO: Temp fix für falsch gerenderte Durchbrüche
        this.height = toFixedFloat(getLengthByAxis(bounds, "y") /*- 0.005*/); //Get group height
        let cutOutsGroup = findChildByName(wallGroup, cutOutsGroupName);
        this.cutOutGroup = createGroup("cutOutGroup", cutOutsGroup); //TODO: Check If not null
        this.cutMesh = this.createCuttingMesh();
        this.cutWallSegment(wallGroup);
        wallGroup.updateMatrix();
        for (let i = 0; i < wallGroup.children.length; i++) {
            wallGroup.children[i].updateMatrix();
        }
        render();
        this.createControlObject(wallGroup, wallMesh.name);

        if (adjustOriginalWall) {
            CutOut.adjustWall(originalWallGroup);
        }

        CutOut.adjustWall(wallGroup);

        recalculateBuildingParameters().then();

        return true;
    }

    getMeshToCut() {
        //TODO: Performance: Liste der BoundingBoxen und mitführen und direkt nur diese schneiden
        let intersect = raycaster.intersectObjects(buildingGroup.children, true);
        for (let i = 0; i < intersect.length - 1; i++) {
            let wallMesh = intersect[i].object;
            //Only use intersected objects that are a mesh and have wallMesh in name
            if (wallMesh.type !== "Mesh" || !wallMesh.name.startsWith("wallMeshBBox")) {
                continue;
            }
            wallMesh = wallMesh.parent;

            //Zu schneidende Wand bestimmen: Entweder die jeweilige Wand im Raum oder die Wand selbst bei Einzelwänden
            let wallGroup = getWallGroupParent(wallMesh);
            if (wallGroup !== null && !isRoofOrFloor(wallGroup)) {
                return wallMesh;
            }
        }

        return null;
    }

    cutWallSegment(cutWall) {
        cutWall.updateMatrix();
        for (let i = 0; i < cutWall.children.length; i++) {
            if (cutWall.children[i].type === "Mesh") {
                cutWall.children[i].geometry.computeBoundingBox();
                cutWall.children[i].updateMatrix();
            }
        }

        // Only the wall, Rähm and Schwelle. The cutout group also holds the flat
        // placement markers (parked at preview height) and earlier cut boxes.
        // Folding those into the box makes it taller on every new opening, and
        // the next door or window is cut shorter than the one before it.
        let zLength = structuralWallHeight(cutWall);
        if (!Number.isFinite(zLength) || zLength <= 0) {
            zLength = cutWall.userData.height;
        }

        let cutMeshZOffset = ((zLength - Globals.defaultRaehmHeight - Globals.defaultSchwelleHeight) - this.height) / 2 + Globals.defaultSchwelleHeight - this.bottomOffset;
        let worldCoords = this.controlObject.getWorldPosition(new Vector3());
        this.cutMesh.rotateX(Math.PI / 2);
        this.cutOutGroup.addWithUuid(this.cutMesh);
        let cutMeshWorldCoords = this.cutMesh.getWorldPosition(new Vector3());
        let diffVector = new Vector3().subVectors(worldCoords, cutMeshWorldCoords);

        if (cutWall.rotation.z === 0) {
            this.cutMesh.position.add(new Vector3(diffVector.x, diffVector.z, 0));
        } else if (Math.abs(cutWall.rotation.z) === Math.PI) {
            this.cutMesh.position.sub(new Vector3(diffVector.x, diffVector.z, 0));
        } else if (cutWall.rotation.z > 0) {
            this.cutMesh.position.add(new Vector3(diffVector.z, -diffVector.x, 0));
        } else {
            this.cutMesh.position.sub(new Vector3(diffVector.z, -diffVector.x, 0));
        }
        this.cutMesh.position.z = cutMeshZOffset;
        this.cutMesh.updateMatrix();

        CutOut.cutWallWithCutMesh(cutWall, this.cutMesh);
    }

    createCuttingMesh() {
        let cuttingMesh = new Mesh(new BoxGeometry(this.width + 0.03, this.height, Globals.HBEWallDefaultThickness), lineMaterial);
        cuttingMesh.geometry.translate(0.015, 0, 0);
        setObjectLayer(cuttingMesh, Globals.layers.hidden);
        cuttingMesh.name = "cutOutCutMesh";
        cuttingMesh.updateMatrix();
        return cuttingMesh;
    }

    createControlObject(wallGroup, wallMeshName) {
        //Die scene Matrix kann wahrscheinlich auch einfach aus der matrixWorld ausgelesen werden, wenn nötig
        this.controlObject.name = controlObjectName;
        // Layout camera looks straight down. A shared depth test lets the wall hide this marker.
        const markerMaterial = controlObjectMaterial.clone();
        markerMaterial.depthTest = false;
        markerMaterial.depthWrite = false;
        this.controlObject.material = markerMaterial;
        this.controlObject.renderOrder = 5;

        //Matrizen berechnen
        this.controlObject.updateMatrix();
        let sceneMatrix = {
            position: this.controlObject.position.clone(),
            rotation: this.controlObject.rotation.clone()
        };

        this.controlObject.removeFromParent();
        this.cutOutGroup.addWithUuid(this.controlObject);
        // Preview rotation is in world space (flat, aligned to the wall). The wall group is already
        // turned up on its edge, so copying that rotation into local space makes the marker edge-on.
        this.cutOutGroup.updateWorldMatrix(true, false);
        const worldQuaternion = new Quaternion().setFromEuler(sceneMatrix.rotation);
        const parentQuaternion = this.cutOutGroup.getWorldQuaternion(new Quaternion());
        this.controlObject.quaternion.copy(parentQuaternion.invert()).multiply(worldQuaternion);
        this.controlObject.position.copy(this.cutOutGroup.worldToLocal(sceneMatrix.position.clone()));
        this.controlObject.updateMatrix();

        this.setControlObjectUserData(wallMeshName, sceneMatrix);

        clearGroup(this.controlObject);
        addEdgeGeometry(this.controlObject);

        setObjectLayer(this.controlObject, Globals.layers.layoutViewOnly, true);
    }

    setControlObjectUserData(wallMeshName, sceneMatrix) {
        this.controlObject.userData.height = this.height;
        this.controlObject.userData.type = "cutOut";
        this.controlObject.userData.originalParent = this.controlObject.parent.uuid;
        this.controlObject.userData.wallMeshName = wallMeshName;
        this.controlObject.userData.marginLeftRight = toFixedFloat(this.marginLeftRight);
        this.controlObject.userData.bottomOffset = toFixedFloat(this.bottomOffset);
        this.controlObject.userData.sceneMatrix = sceneMatrix;
        this.controlObject.userData.groupMatrix = {
            position: this.controlObject.position.clone(),
            rotation: this.controlObject.rotation.clone()
        };
    }
}
